import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
    Card, CardContent, CardHeader,
    CardTitle, CardDescription
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, Lock, Sparkles, ArrowLeft, ShieldCheck, AlertCircle } from "lucide-react";
import { apiClient } from "@/services/apiClient";
import { API_URLS, IN_APP_ROUTES } from "@/constants/urls";
import { apiErrorHandler } from "@/helpers/apiErrorHandler";

export default function ResetPasswordPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // JWT token is expected as ?token=<jwt> in the URL
    const token = searchParams.get("token");

    const [formData, setFormData] = useState({
        password: "",
        passwordConfirm: "",
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [tokenValid, setTokenValid] = useState(true); // assume valid until proven otherwise

    // If no token present in URL, mark invalid immediately
    useEffect(() => {
        if (!token) {
            setTokenValid(false);
        }
    }, [token]);

    const handleChange = (e) => {
        setFormData((prev) => ({
            ...prev,
            [e.target.name]: e.target.value,
        }));
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();

        if (formData.password !== formData.passwordConfirm) {
            toast.error("Passwords do not match");
            return;
        }

        if (formData.password.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }

        setIsLoading(true);

        try {
            const result = await apiClient.post(API_URLS.RESET_PASSWORD, {
                token,
                password: formData.password,
                password_confirm: formData.passwordConfirm,
            });

            if (result.status === 200) {
                toast.success("Password reset successfully!");
                navigate(IN_APP_ROUTES.LOGIN_PAGE);
            }
        } catch (error) {
            // If backend returns 401/400 for invalid/expired token, surface that clearly
            if (error?.response?.status === 401 || error?.response?.status === 400) {
                setTokenValid(false);
            } else {
                apiErrorHandler(error);
            }
        }

        setIsLoading(false);
    };

    return (
        <div className="min-h-screen flex">

            {/* Left Side — identical to Register/Login/ForgotPassword */}
            <div className="hidden lg:flex lg:w-1/2 hero-gradient relative overflow-hidden">
                <div className="relative z-10 flex flex-col justify-center px-12 lg:px-16">

                    <div className="flex items-center gap-3 mb-12">
                        <div className="w-12 h-12 rounded-xl bg-primary-foreground/10 backdrop-blur-sm flex items-center justify-center font-bold text-xl text-primary-foreground border border-primary-foreground/20">
                            P
                        </div>
                        <span className="font-semibold text-2xl text-primary-foreground">
                            Prowen
                        </span>
                    </div>

                    <h1 className="text-4xl lg:text-5xl font-bold text-primary-foreground mb-6 leading-tight">
                        Make Smarter<br />
                        <span className="text-accent-light">Pricing Decisions</span>
                    </h1>

                    <p className="text-lg text-primary-foreground/80 mb-8 max-w-md">
                        Real-time competitor tracking, review monitoring, and market trend analysis for modern hotel portfolios.
                    </p>

                    <div className="space-y-4">
                        {[
                            "Competitor Rate Tracking",
                            "Review & Rankings Monitoring",
                            "Real-Time Market Trends",
                            "AI-Powered Insights",
                        ].map((feature, index) => (
                            <div key={index} className="flex items-center gap-3 text-primary-foreground/80">
                                <div className="w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center">
                                    <Sparkles className="w-3 h-3 text-accent" />
                                </div>
                                <span>{feature}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
                <div className="absolute top-20 right-20 w-64 h-64 bg-primary-light/10 rounded-full blur-3xl" />
            </div>

            {/* Right Side */}
            <div className="flex-1 flex items-center justify-center p-8 bg-background">
                <div className="w-full max-w-md">

                    {/* Mobile Logo */}
                    <div className="flex items-center gap-3 mb-8 lg:hidden">
                        <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center font-bold text-primary-foreground">
                            P
                        </div>
                        <span className="font-semibold text-xl text-foreground">
                            Prowen
                        </span>
                    </div>

                    <Card className="border-0 shadow-xl">
                        <CardHeader className="space-y-1 pb-6">
                            <CardTitle className="text-2xl">Reset Password</CardTitle>
                            <CardDescription>
                                {tokenValid
                                    ? "Enter your new password below"
                                    : "This reset link is invalid or has expired"}
                            </CardDescription>
                        </CardHeader>

                        <CardContent>
                            {!tokenValid ? (
                                /* Invalid / Expired Token State */
                                <div className="flex flex-col items-center text-center space-y-4 py-4">
                                    <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                                        <AlertCircle className="w-8 h-8 text-red-600" />
                                    </div>
                                    <div className="space-y-1">
                                        <p className="font-medium text-foreground">Link invalid or expired</p>
                                        <p className="text-sm text-muted-foreground">
                                            This password reset link is no longer valid. Reset links expire after a short period for security.
                                        </p>
                                    </div>
                                    <Link to="/forgot-password">
                                        <Button variant="outline" className="mt-2">
                                            Request a new link
                                        </Button>
                                    </Link>
                                </div>
                            ) : (
                                /* Reset Form */
                                <form onSubmit={handleResetPassword} className="space-y-4">

                                    {/* Password strength hint */}
                                    <div className="flex items-start gap-2 rounded-lg bg-muted/50 px-3 py-2.5 text-xs text-muted-foreground">
                                        <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                                        <span>
                                            Use at least 8 characters with a mix of letters, numbers, and symbols for a strong password.
                                        </span>
                                    </div>

                                    {/* New Password */}
                                    <div className="space-y-2">
                                        <Label>New Password</Label>
                                        <div className="relative">
                                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                            <Input
                                                name="password"
                                                type={showPassword ? "text" : "password"}
                                                value={formData.password}
                                                onChange={handleChange}
                                                placeholder="Enter new password"
                                                className="pl-10 pr-10"
                                                required
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2"
                                            >
                                                {showPassword ? (
                                                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                                                ) : (
                                                    <Eye className="h-4 w-4 text-muted-foreground" />
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Confirm Password */}
                                    <div className="space-y-2">
                                        <Label>Confirm New Password</Label>
                                        <div className="relative">
                                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                            <Input
                                                name="passwordConfirm"
                                                type={showConfirmPassword ? "text" : "password"}
                                                value={formData.passwordConfirm}
                                                onChange={handleChange}
                                                placeholder="Confirm new password"
                                                className="pl-10 pr-10"
                                                required
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2"
                                            >
                                                {showConfirmPassword ? (
                                                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                                                ) : (
                                                    <Eye className="h-4 w-4 text-muted-foreground" />
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    <Button
                                        type="submit"
                                        className="w-full"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? "Resetting password..." : "Reset Password"}
                                    </Button>
                                </form>
                            )}

                            <div className="mt-6 flex justify-center">
                                <Link
                                    to="/login"
                                    className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    <ArrowLeft className="w-4 h-4" />
                                    Back to Sign In
                                </Link>
                            </div>
                        </CardContent>
                    </Card>

                    <p className="text-center text-xs text-muted-foreground mt-6">
                        © {new Date().getFullYear()} Prowen Technologies India Private Limited
                    </p>
                </div>
            </div>
        </div>
    );
}